import type { ReactNode, SVGProps } from 'react';

/**
 * Lucide-derived inline SVG icon set, ported from
 * apps/admin-web/design-reference/js/icons.jsx.  All icons render as
 * `<svg class="icon …">` so existing component CSS that targets
 * `.icon { width: 16px; height: 16px }` continues to apply.
 */

type SvgGeometry = ReactNode;

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  readonly size?: number;
  readonly strokeWidth?: number;
}

function makeIcon(geometry: SvgGeometry, viewBox = '0 0 24 24'): React.FC<IconProps> {
  const Component: React.FC<IconProps> = ({
    size = 16,
    strokeWidth = 1.75,
    className,
    ...rest
  }) => (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`icon ${className ?? ''}`.trim()}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {geometry}
    </svg>
  );
  return Component;
}

export const Icon = {
  Mail: makeIcon(
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 6L2 7" />
    </>,
  ),
  MailOpen: makeIcon(
    <>
      <path d="M21 8.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8.5" />
      <path d="m21 8.5-9-5-9 5" />
      <path d="m3 19 7-5" />
      <path d="m21 19-7-5" />
      <path d="m3 8.5 9 6 9-6" />
    </>,
  ),
  Plus: makeIcon(<path d="M12 5v14M5 12h14" />),
  Search: makeIcon(
    <>
      <circle cx="11" cy="11" r="7.5" />
      <path d="m21 21-4.3-4.3" />
    </>,
  ),
  Filter: makeIcon(<path d="M3 5h18l-7 9v6l-4-2v-4Z" />),
  Refresh: makeIcon(
    <>
      <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
      <path d="M21 3v5h-5" />
    </>,
  ),
  Eye: makeIcon(
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>,
  ),
  EyeOff: makeIcon(
    <>
      <path d="M9.9 5.1A11.6 11.6 0 0 1 12 5c6.5 0 10 7 10 7a13.4 13.4 0 0 1-3.4 4.3" />
      <path d="M6.7 6.7C3.6 8.6 2 12 2 12s3.5 7 10 7c1.7 0 3.2-.3 4.6-.9" />
      <path d="M14.1 14.1A3 3 0 0 1 9.9 9.9" />
      <path d="m2 2 20 20" />
    </>,
  ),
  Copy: makeIcon(
    <>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </>,
  ),
  Trash: makeIcon(
    <>
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </>,
  ),
  Check: makeIcon(<path d="m5 12 5 5L20 7" />),
  CheckCircle: makeIcon(
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </>,
  ),
  X: makeIcon(<path d="M18 6 6 18M6 6l12 12" />),
  Chevron: makeIcon(<path d="m6 9 6 6 6-6" />),
  ChevronRight: makeIcon(<path d="m9 6 6 6-6 6" />),
  ChevronLeft: makeIcon(<path d="m15 6-6 6 6 6" />),
  Arrow: makeIcon(<path d="M5 12h14M13 5l7 7-7 7" />),
  ArrowUp: makeIcon(<path d="M12 19V5M5 12l7-7 7 7" />),
  ArrowDn: makeIcon(<path d="M12 5v14M5 12l7 7 7-7" />),
  ArrowUR: makeIcon(<path d="M7 17 17 7M8 7h9v9" />),
  ArrowDL: makeIcon(<path d="M17 7 7 17M16 17H7V8" />),
  Back: makeIcon(<path d="M19 12H5M12 19l-7-7 7-7" />),
  Clock: makeIcon(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>,
  ),
  Globe: makeIcon(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </>,
  ),
  Coins: makeIcon(
    <>
      <circle cx="9" cy="9" r="6" />
      <path d="M9 6v6l3 1" />
      <path d="M16.5 8a6 6 0 0 1 0 11h-7" />
    </>,
  ),
  Wallet: makeIcon(
    <>
      <path d="M3 7v10a3 3 0 0 0 3 3h13a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H6a3 3 0 0 1 0-6h12v6" />
      <circle cx="17" cy="13" r="1.2" fill="currentColor" />
    </>,
  ),
  Card: makeIcon(
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20M6 15h4" />
    </>,
  ),
  Bitcoin: makeIcon(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 7h5a2.5 2.5 0 0 1 0 5H9zM9 12h5.5a2.5 2.5 0 0 1 0 5H9zM10 5v2M14 5v2M10 17v2M14 17v2" />
    </>,
  ),
  Bell: makeIcon(
    <>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 8 3 8H3s3-1 3-8" />
      <path d="M10 21a2 2 0 0 0 4 0" />
    </>,
  ),
  User: makeIcon(
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>,
  ),
  Users: makeIcon(
    <>
      <circle cx="9" cy="8" r="4" />
      <path d="M2 21a7 7 0 0 1 14 0" />
      <path d="M16 3.5a4 4 0 0 1 0 7.7" />
      <path d="M22 21a6.5 6.5 0 0 0-4-6" />
    </>,
  ),
  Settings: makeIcon(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 9 4.6 1.7 1.7 0 0 0 10 3.1V3a2 2 0 1 1 4 0v.1A1.7 1.7 0 0 0 15 4.6a1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1A1.7 1.7 0 0 0 19.4 9c.2.5.7 1 1.5 1H21a2 2 0 1 1 0 4h-.1c-.6 0-1.2.4-1.5 1Z" />
    </>,
  ),
  Logout: makeIcon(
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5M21 12H9" />
    </>,
  ),
  Lock: makeIcon(
    <>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>,
  ),
  Key: makeIcon(
    <>
      <path d="M14.5 7.5a4 4 0 1 1-5.7 5.7l-6.3 6.3v3h3l1-1v-2h2v-2h2l3.3-3.3a4 4 0 0 1 .7-8.7Z" />
      <circle cx="16.5" cy="7.5" r="1.2" fill="currentColor" />
    </>,
  ),
  Code: makeIcon(<path d="m16 18 6-6-6-6M8 6l-6 6 6 6" />),
  Doc: makeIcon(
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8M8 17h6" />
    </>,
  ),
  Info: makeIcon(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01M11 12h1v5h1" />
    </>,
  ),
  Warn: makeIcon(
    <>
      <path d="M10.3 3.7 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.7a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </>,
  ),
  Shield: makeIcon(<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />),
  Sparkle: makeIcon(<path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.5 5.5l2 2M16.5 16.5l2 2M5.5 18.5l2-2M16.5 7.5l2-2" />),
  Sun: makeIcon(
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>,
  ),
  Moon: makeIcon(<path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />),
  Menu: makeIcon(<path d="M3 6h18M3 12h18M3 18h18" />),
  Send: makeIcon(
    <>
      <path d="M22 2 11 13" />
      <path d="m22 2-7 20-4-9-9-4Z" />
    </>,
  ),
  Inbox: makeIcon(
    <>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.5 5h13l3.5 7v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6Z" />
    </>,
  ),
  Headset: makeIcon(
    <>
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1v-6h3ZM3 19a2 2 0 0 0 2 2h1v-6H3Z" />
    </>,
  ),
  Bolt: makeIcon(<path d="M13 2 3 14h8l-1 8 10-12h-8Z" />),
  Server: makeIcon(
    <>
      <rect x="2" y="3" width="20" height="8" rx="2" />
      <rect x="2" y="13" width="20" height="8" rx="2" />
      <path d="M6 7h.01M6 17h.01" />
    </>,
  ),
  Activity: makeIcon(<path d="M22 12h-4l-3 9-6-18-3 9H2" />),
  Trend: makeIcon(
    <>
      <path d="m3 17 6-6 4 4 8-8" />
      <path d="M14 7h7v7" />
    </>,
  ),
  Hash: makeIcon(<path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" />),
  Pencil: makeIcon(
    <>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </>,
  ),
  Cancel: makeIcon(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M5.6 5.6l12.8 12.8" />
    </>,
  ),
  Loader: makeIcon(
    <>
      <path d="M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8" />
    </>,
  ),
  Fire: makeIcon(<path d="M9 13c0-3 3-5 3-9 4 4 6 7 6 11a6 6 0 0 1-12 0c0-2 1-3 2-4 0 1 .5 2 1 2Z" />),
  Coin: makeIcon(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 7h6M9 17h6M12 7v10M9.5 11.5h5" />
    </>,
  ),
  Github: makeIcon(<path d="M12 2a10 10 0 0 0-3.2 19.5c.5.1.7-.2.7-.5v-2c-2.8.6-3.4-1.2-3.4-1.2-.5-1.2-1.1-1.5-1.1-1.5-.9-.6.1-.6.1-.6 1 .1 1.5 1 1.5 1 .9 1.6 2.4 1.1 3 .9.1-.7.4-1.1.7-1.4-2.2-.3-4.6-1.1-4.6-5 0-1.1.4-2 1-2.7-.1-.3-.4-1.3.1-2.7 0 0 .8-.3 2.7 1a9.4 9.4 0 0 1 5 0c1.9-1.3 2.7-1 2.7-1 .5 1.4.2 2.4.1 2.7.6.7 1 1.6 1 2.7 0 3.9-2.4 4.7-4.6 5 .4.3.7.9.7 1.8v2.7c0 .3.2.6.7.5A10 10 0 0 0 12 2Z" />),
} as const;

export type IconName = keyof typeof Icon;
